"use client"
import { Image } from "@chakra-ui/react"
import type { EmblaCarouselType, EmblaEventType } from "embla-carousel"
import Autoplay from "embla-carousel-autoplay"
import useEmblaCarousel from "embla-carousel-react"
import { memo, useCallback, useEffect, useRef, useState } from "react"

const TWEEN_FACTOR_BASE = 0.2

type Props = {
  slides: {
    id: number
    image: string
    title?: string
  }[]
}

const Parallax = ({ slides }: Props) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      dragFree: false,
      containScroll: "trimSnaps",
    },
    [Autoplay({ delay: 4000, stopOnInteraction: false })],
  )

  const tweenFactor = useRef(0)
  const tweenNodes = useRef<HTMLElement[]>([])
  const [selectedIndex, setSelectedIndex] = useState(0)

  const setTweenNodes = useCallback((emblaApi: EmblaCarouselType): void => {
    tweenNodes.current = emblaApi.slideNodes().map((slideNode) => {
      return slideNode.querySelector(".embla__parallax__layer") as HTMLElement
    })
  }, [])

  const setTweenFactor = useCallback((emblaApi: EmblaCarouselType) => {
    tweenFactor.current = TWEEN_FACTOR_BASE * emblaApi.scrollSnapList().length
  }, [])

  const tweenParallax = useCallback(
    (emblaApi: EmblaCarouselType, eventName?: EmblaEventType) => {
      const engine = emblaApi.internalEngine()
      const scrollProgress = emblaApi.scrollProgress()
      const slidesInView = emblaApi.slidesInView()
      const isScrollEvent = eventName === "scroll"

      emblaApi.scrollSnapList().forEach((scrollSnap, snapIndex) => {
        let diffToTarget = scrollSnap - scrollProgress
        const slidesInSnap = engine.slideRegistry[snapIndex]

        slidesInSnap.forEach((slideIndex) => {
          if (isScrollEvent && !slidesInView.includes(slideIndex)) return

          if (engine.options.loop) {
            engine.slideLooper.loopPoints.forEach((loopItem) => {
              const target = loopItem.target()

              if (slideIndex === loopItem.index && target !== 0) {
                const sign = Math.sign(target)

                if (sign === -1) {
                  diffToTarget = scrollSnap - (1 + scrollProgress)
                }
                if (sign === 1) {
                  diffToTarget = scrollSnap + (1 - scrollProgress)
                }
              }
            })
          }

          const translate = diffToTarget * (-1 * tweenFactor.current) * 100
          const tweenNode = tweenNodes.current[slideIndex]
          tweenNode.style.transform = `translateX(${translate}%)`
        })
      })
    },
    [],
  )

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [])

  const scrollTo = useCallback(
    (index: number) => emblaApi?.scrollTo(index),
    [emblaApi],
  )

  useEffect(() => {
    if (!emblaApi) return

    setTweenNodes(emblaApi)
    setTweenFactor(emblaApi)
    tweenParallax(emblaApi)
    onSelect(emblaApi)

    emblaApi
      .on("reInit", setTweenNodes)
      .on("reInit", setTweenFactor)
      .on("reInit", tweenParallax)
      .on("reInit", onSelect)
      .on("scroll", tweenParallax)
      .on("slideFocus", tweenParallax)
      .on("select", onSelect)
  }, [emblaApi, tweenParallax, onSelect])

  return (
    <div
      className="embla"
      style={{ height: "100vh", overflow: "hidden", position: "relative" }}
    >
      <div
        className="embla__viewport"
        ref={emblaRef}
        style={{ height: "100%" }}
      >
        <div
          className="embla__container"
          style={{ display: "flex", height: "100%" }}
        >
          {slides.map((slide) => (
            <div
              key={slide.id}
              className="embla__slide"
              style={{
                flex: "0 0 100%",
                minWidth: 0,
                position: "relative",
                height: "100vh",
              }}
            >
              <div
                className="embla__parallax"
                style={{ height: "100%", overflow: "hidden" }}
              >
                <div
                  className="embla__parallax__layer"
                  style={{ height: "100%", width: "120%" }}
                >
                  <Image
                    src={slide.image}
                    alt={slide.title}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      objectPosition: "center",
                      display: "block",
                    }}
                  />
                </div>
              </div>
              {slide.title && (
                <div
                  style={{
                    position: "absolute",
                    bottom: "50px",
                    left: "50px",
                    color: "white",
                    fontSize: "2rem",
                    fontWeight: "bold",
                    textShadow: "2px 2px 4px rgba(0,0,0,0.7)",
                    zIndex: 1,
                  }}
                >
                  {slide.title}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Dot Indicators */}
      <div
        style={{
          position: "absolute",
          bottom: "30px",
          left: "30px",
          display: "flex",
          gap: "10px",
          zIndex: 2,
        }}
      >
        {slides.map((_, index) => (
          <button
            key={index}
            type="button"
            onClick={() => scrollTo(index)}
            style={{
              width: "12px",
              height: "12px",
              borderRadius: "50%",
              border: "none",
              backgroundColor:
                index === selectedIndex ? "white" : "rgba(255, 255, 255, 0.4)",
              cursor: "pointer",
              transition: "all 0.3s ease",
              transform: index === selectedIndex ? "scale(1.2)" : "scale(1)",
              boxShadow: "0 0 10px 0 rgba(0, 0, 0, 0.5)",
            }}
            onMouseEnter={(e) => {
              if (index !== selectedIndex) {
                e.currentTarget.style.backgroundColor =
                  "rgba(255, 255, 255, 0.7)"
                e.currentTarget.style.transform = "scale(1.1)"
              }
            }}
            onMouseLeave={(e) => {
              if (index !== selectedIndex) {
                e.currentTarget.style.backgroundColor =
                  "rgba(255, 255, 255, 0.4)"
                e.currentTarget.style.transform = "scale(1)"
              }
            }}
          />
        ))}
      </div>
    </div>
  )
}

export default memo(Parallax)
